/**
 * The server's rules, restated so this feature agrees with it before submitting.
 *
 * Mirrors `secrets/validation.ts`'s framing: this decides nothing, every rule here
 * is mirrored from the service, and the service is authoritative. Nothing here
 * gates the submit button.
 *
 * `SLUG_PATTERN`/`MAX_SLUG_LENGTH` are no longer surfaced to a person — the slug
 * is generated for them (see `slug.ts`) — but the generator still has to satisfy
 * the same server-side rule, so the mirrored constants stay here.
 *
 * Each constant names its counterpart in
 * `product-x-next-service/src/app/features/llm_profiles/validation.py`.
 */

/** Mirrors `validation.SLUG_PATTERN`. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Mirrors `validation.MAX_SLUG_LENGTH`. */
export const MAX_SLUG_LENGTH = 64;

/** Mirrors `validation.MAX_NAME_LENGTH`. */
export const MAX_NAME_LENGTH = 100;

/**
 * A problem with the name, or `null`.
 *
 * Counted in code points via `[...text].length`, matching `secrets/validation.ts`'s
 * `validateDescription` — the backend's Python `len()` counts code points too, so a
 * mirror counting UTF-16 code units would refuse (or accept) names the service
 * disagrees with for any name carrying an astral character.
 */
export function validateName(name: string): string | null {
  const text = name.trim();
  if (!text) return 'Enter a name.';
  const length = [...text].length;
  if (length > MAX_NAME_LENGTH) {
    return `Name is ${String(length)} characters; the maximum is ${String(MAX_NAME_LENGTH)}.`;
  }
  return null;
}

/** A problem with the model, or `null`. */
export function validateModel(model: string): string | null {
  return model.trim() ? null : 'Enter a model.';
}

/**
 * A problem with the Base URL, or `null`.
 *
 * Blank is valid here — unlike name/model, this field has a "no override" state
 * (spec 071) — mirrors `validation.validate_base_url` accepting `None`. A
 * non-blank value is checked with the platform's own `URL` constructor
 * (throws on anything that is not a well-formed absolute URL) rather than a
 * hand-rolled regex, the same "prefer the platform's own check" reasoning the
 * backend applies with `pydantic.AnyUrl`.
 */
export function validateBaseUrl(baseUrl: string): string | null {
  const text = baseUrl.trim();
  if (!text) return null;
  try {
    new URL(text);
  } catch {
    return 'Enter a well-formed URL, or leave it blank.';
  }
  return null;
}
